import { useState, useMemo } from 'react'; // Removed useEffect
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';

const CreateStream = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // user object now contains the token
  const [loading, setLoading] = useState(false);
  // We only support Twitch now, so this is a constant
  const streamSource = 'twitch';
  const [twitchChannelLink, setTwitchChannelLink] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledStartTime, setScheduledStartTime] = useState('');

  // Calculate min datetime for schedule input (current time + 5 minutes)
  const minScheduleTime = useMemo(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Add 5 minutes buffer
    // Format to YYYY-MM-DDTHH:mm required by datetime-local
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);


  // No longer need to generate a stream key since we're only using Twitch

  const handleCreateStream = async (e) => {
    e.preventDefault();
    // Check for user connection AND token availability
    if (!user?.isConnected || !user?.token) {
      toast.error('Authentication token not found. Please reconnect wallet or wait.');
      return;
    }

    setLoading(true); // Show loading indicator during submission

    const payload = {
      title: e.target.title.value,
      description: e.target.description.value,
      category: e.target.category.value,
      game: e.target.category.value, // Assuming category is the game for now
      tags: e.target.tags.value.split(',').map(tag => tag.trim()).filter(tag => tag), // Filter empty tags
      streamSource: streamSource,
      thumbnailUrl: thumbnailUrl || undefined, // Send only if not empty
      status: isScheduled ? 'scheduled' : 'live',
    };

    if (isScheduled) {
      if (!scheduledStartTime) {
        toast.error('Please select a scheduled start time.');
        setLoading(false);
        return;
      }
      // Validate schedule time is in the future
      if (new Date(scheduledStartTime) <= new Date()) {
        toast.error('Scheduled time must be in the future.');
        setLoading(false);
        return;
      }
      payload.scheduledStartTime = scheduledStartTime;
    }


    // Only Twitch is supported now
    if (!twitchChannelLink) {
      toast.error('Please enter your Twitch channel link.');
      setLoading(false);
      return;
    }
    // Basic validation for Twitch link (can be improved)
    if (!twitchChannelLink.toLowerCase().includes('twitch.tv/')) {
        toast.error('Please enter a valid Twitch channel link (e.g., https://twitch.tv/yourchannel).');
        setLoading(false);
        return;
    }
    payload.twitchChannelLink = twitchChannelLink;


    try {
      const response = await fetch(`${API_BASE_URL}/api/streams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${user.token}` // Use token from context
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create stream' }));
        throw new Error(errorData.message || 'Failed to create stream');
      }

      const data = await response.json();
      console.log('Received data after stream creation:', data); // Log the received data
      toast.success('Stream created successfully!');

      // Check if streamId exists before navigating
      if (data && data.streamId) {
        navigate(`/live/${data.streamId}`);
      } else {
        console.error('Stream ID not found in response data:', data);
        toast.error('Failed to get stream ID after creation.');
      }
    } catch (error) {
      console.error('Error creating stream:', error);
      toast.error(`Failed to create stream: ${error.message}`);
    } finally {
      setLoading(false); // Hide loading indicator
    }
  };

  if (!user?.isConnected) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Connect Your Wallet</h2>
          <p className="text-gray-400 mb-6">Please connect your wallet to create a stream</p>
          {/* @ts-expect-error Temporary fix for React 19 */}
          <appkit-button />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading stream setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Create Stream</h1>

        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <form onSubmit={handleCreateStream} className="space-y-6">
             {/* Twitch Channel Link */}
             <div>
                <label htmlFor="twitchChannelLink" className="block text-gray-300 mb-2">Twitch Channel Link</label>
                <input
                  type="url"
                  id="twitchChannelLink"
                  name="twitchChannelLink"
                  required
                  value={twitchChannelLink}
                  onChange={(e) => setTwitchChannelLink(e.target.value)}
                  className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                  placeholder="https://twitch.tv/yourchannel"
                />
              </div>

             {/* Standard Stream Details */}
            <div>
              <label htmlFor="title" className="block text-gray-300 mb-2">Stream Title</label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="Enter stream title"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-gray-300 mb-2">Description</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="Enter stream description"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-gray-300 mb-2">Category</label>
              <select
                id="category"
                name="category"
                required
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
              >
                <option value="gaming">Gaming</option>
                <option value="esports">Esports</option>
                <option value="crypto">Crypto</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="tags" className="block text-gray-300 mb-2">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="gaming, crypto, esports"
              />
            </div>

            {/* Thumbnail URL Input */}
            <div>
              <label htmlFor="thumbnailUrl" className="block text-gray-300 mb-2">Thumbnail URL (Optional)</label>
              <input
                type="url"
                id="thumbnailUrl"
                name="thumbnailUrl"
                value={thumbnailUrl}
                onChange={(e) => setThumbnailUrl(e.target.value)}
                className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                placeholder="https://example.com/image.png"
              />
            </div>

            {/* Scheduling Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="scheduleToggle"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                className="form-checkbox h-5 w-5 text-primary bg-dark border-gray-700 rounded focus:ring-primary mr-2"
              />
              <label htmlFor="scheduleToggle" className="text-gray-300">Schedule for Later</label>
            </div>

            {/* Scheduled Start Time Input (Conditional) */}
            {isScheduled && (
              <div>
                <label htmlFor="scheduledStartTime" className="block text-gray-300 mb-2">Scheduled Start Time</label>
                <input
                  type="datetime-local"
                  id="scheduledStartTime"
                  name="scheduledStartTime"
                  required={isScheduled}
                  value={scheduledStartTime}
                  onChange={(e) => setScheduledStartTime(e.target.value)}
                  min={minScheduleTime} // Prevent selecting past dates/times
                  className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300 focus:outline-none focus:border-primary"
                />
              </div>
            )}


            <button
              type="submit"
              className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Create Stream {loading && <span className="ml-2 animate-spin">⚙️</span>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateStream;
